import React, { useState, useEffect } from "react";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
  Modal,
} from "@mui/material";
import { getOBTManagementInfo, saveOBTInfo } from "../../services/userServices";
import { useNavigate, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import classes from "./IDMOGOwnership.module.css";
import IDMSupportModalContent from "components/IDMSupportModalContent/IDMSupportModalContent";
import OnboardingModalContent from "components/OnboardingModalContent/OnboardingModalContent";
import OffboardingModalContent from "components/OffboardingModalContent/OffboardingtModalContent";
import AccessModalContent from "components/AccessModalContent/AccessModalContent";
import OBTRequirementsModalContent from "components/OBTRequirementsModalContent/OBTRequirementsModalContent";
import TASupportModalContent from "components/TASupportModalContent/TASupportModalContent";
import SaveBSTaskDetails from "components/OBTBusinessServiceTask/SaveBSTaskDetails";
import EditByRowIndex from "./EditByRowIndex";
import { useDispatch, useSelector } from "react-redux";
import { obtInfoManagementActions } from "Store/obtInfoManagement";
import LaunchIcon from "@mui/icons-material/Launch";
import PropTypes from "prop-types";
import applicationUsageLog from "../../utils/applicationUsagesLog";
import { useMemo } from "react";
import { useAuth } from "context/AuthContext";
import LDSupportModalContent from "components/LDSupportModalContent/LDSupportModalContent";
const CustomIconButton = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  textDecoration: "none",
  color: theme.palette.primary.main,
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
    color: theme.palette.primary.dark,
  },
  "& .MuiTypography-root": {
    marginRight: theme.spacing(1),
  },
}));

const IconButtonWithLabel = ({ onClick, label }) => (
  <CustomIconButton onClick={onClick}>
    <Typography variant="body2">{label}</Typography>
    <LaunchIcon />
  </CustomIconButton>
);

IconButtonWithLabel.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 34,
  height: 20,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 18,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(11px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2.5,
    "&.Mui-checked": {
      transform: "translateX(14.5px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#177ddc" : "#3299FF",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 14.5,
    height: 14.5,
    borderRadius: 7.25,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 20 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,.35)"
        : "rgba(0,0,0,.25)",
    boxSizing: "border-box",
  },
}));

const MAX_BULK_EDIT_RECORDS = 50;

function IDMOGOwnerShip() {
  const { hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const defaultFilterProps = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    clientDesc: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    programDesc: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    projectDesc: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    projectCodeFull: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    idmDeploymentStatus: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    rampDownDate: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    rosterLockBusDays: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    rosterLockFrom: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    access: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
  };

  const dispatch = useDispatch();
  const details = useSelector((state) => state.obtInfoManagement);

  const [open, setOpen] = useState(false);
  const [parentClientList, setParentClientList] = useState([]);
  const [selectedParentClient, setSelectedParentClient] = useState("");
  const [showRecordByStatus, setShowRecordByStatus] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [editAccess, setEditAccess] = useState(false);
  const [modalType, setModalType] = useState("");
  const [projectFullCode, setProjectFullCode] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [gridPageSize, setGridPageSize] = useState(10);
  const [filterProps, setFilterProps] = useState(defaultFilterProps);

  const columns = [
    ...(editAccess
      ? [
          {
            selectionMode: "multiple",
            frozen: false,
            align: "center",
            bodyStyle: { justifyContent: "center" },
          },
        ]
      : []),
    {
      field: "rowIndex",
      header: "Row #",
      filter: false,
      sortable: false,
      frozen: false,
      align: "center",
      bodyStyle: { textAlign: "right" },
    },
    {
      field: "clientDesc",
      header: "Client",
      filter: true,
      sortable: true,
      frozen: false,
      filterPlaceholder: "Search by Client",
      align: "center",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "programDesc",
      header: "Program",
      filter: true,
      sortable: true,
      frozen: false,
      filterPlaceholder: "Search by Program",
      align: "center",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "projectDesc",
      header: "Project",
      filter: true,
      sortable: true,
      frozen: false,
      filterPlaceholder: "Search by Project",
      align: "center",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "projectCodeFull",
      header: "Full Project Code",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Full Project Code",
      align: "center",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "idmDeploymentStatus",
      header: "IDM Deployment Status",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by IDM Deployment Status",
      align: "center",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "rampDownDate",
      header: "Ramp Down Date",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Ramp Down Date",
      align: "center",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "rosterLockBusDays",
      header: "Roster Lock (Business Days)",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Roster Lock (Business Days)",
      align: "center",
      bodyStyle: { textAlign: "right" },
    },
    {
      field: "rosterLockFrom",
      header: "Roster Lock From",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Roster Lock From",
      align: "center",
      bodyStyle: { textAlign: "left" },
    },
    {
      field: "idmSupport",
      header: "IDM Support",
      sortable: false,
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <IconButtonWithLabel
          onClick={() => {
            setModalType("idmSupport");
            openInPopup(params, "idmSupport");
            dispatch(obtInfoManagementActions.setAllValues(params));
          }}
          label="View Tasks"
        />
      ),
    },
    {
      field: "ogSupported",
      header: "Onboarding Guide Support",
      sortable: false,
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <IconButtonWithLabel
          onClick={() => {
            setModalType("ogguidesupport");
            openInPopup(params, "ogguidesupport");
            dispatch(obtInfoManagementActions.setAllValues(params));
          }}
          label="View Tasks"
        />
      ),
    },
    {
      field: "ldSupport",
      header: "LD Support",
      sortable: false,
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <IconButtonWithLabel
          onClick={() => {
            setModalType("ldsupport");
            openInPopup(params, "ldsupport");
            dispatch(obtInfoManagementActions.setAllValues(params));
          }}
          label="View Tasks"
        />
      ),
    },
    {
      field: "taSupport",
      header: "TA Support",
      sortable: false,
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <IconButtonWithLabel
          onClick={() => {
            setModalType("tasupport");
            openInPopup(params, "tasupport");
            dispatch(obtInfoManagementActions.setAllValues(params));
          }}
          label="View Tasks"
        />
      ),
    },

    {
      field: "offborar",
      header: "Offboarding Notification",
      sortable: false,
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <IconButtonWithLabel
          onClick={() => {
            setModalType("offboardingnotification");
            openInPopup(params, "offboardingnotification");
            dispatch(obtInfoManagementActions.setAllValues(params));
          }}
          label="View Tasks"
        />
      ),
    },
    {
      field: "bsTask",
      header: "Business Services",
      sortable: false,
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <IconButtonWithLabel
          onClick={() => {
            setModalType("buisnessservices");
            openInPopup(params, "buisnessservices");
            dispatch(obtInfoManagementActions.setAllValues(params));
          }}
          label="View Tasks"
        />
      ),
    },
    {
      field: "access",
      header: "Access",
      sortable: false,
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <IconButtonWithLabel
          onClick={() => {
            setModalType("access");
            openInPopup(params, "access");
            dispatch(obtInfoManagementActions.setAllValues(params));
          }}
          label="View Tasks"
        />
      ),
    },
    {
      field: "obtRequirements",
      header: "OBT Requirements",
      sortable: false,
      align: "center",
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <IconButtonWithLabel
          onClick={() => {
            setModalType("obtrequirements");
            openInPopup(params, "obtrequirements");
            dispatch(obtInfoManagementActions.setAllValues(params));
          }}
          label="View Tasks"
        />
      ),
    },
  ];

  const calculatePageSize = () => {
    const height = window.innerHeight;
    if (height > 1200) return 100;
    if (height > 900) return 50;
    if (height > 600) return 25;
    return 10;
  };

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        if (!cancelled) {
          setLoading(true);

          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "OnboardingInformation",
            "IDM OG Ownership",
          );

          if (hasAnyPermission(["WFM_IDM_Group_Admin", "OG_Group"])) {
            setEditAccess(true);
          }
        }

        let selectedParentClientDesc = { parentClientDesc: "" };

        if (location?.state?.parentClient) {
          window.history.replaceState({}, document.title);
          const parentClient = location.state.parentClient.toString();

          if (!cancelled) {
            setSelectedParentClient({ parentClientDesc: parentClient });
          }

          selectedParentClientDesc = { parentClientDesc: parentClient };
        }

        await fetchData(selectedParentClientDesc);
      } catch (error) {
        if (!cancelled) {
          setSnack({
            open: true,
            message: "Error occurred while fetching required data.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [
    location.state?.fromLogin,
    location.state?.parentClient,
    hasAnyPermission,
  ]);
  const fetchData = async (selectedParentClientValue) => {
    try {
      setLoading(true);
      const response = await getOBTManagementInfo(
        selectedParentClientValue.parentClientDesc,
      );
      if (response.status) {
        const parentClientsData =
          response.data?.parentClientDescs?.map((item) => ({
            parentClientDesc: item?.parentClientDesc,
          })) || [];

        const uniqueParentClients = Array.from(
          new Set(parentClientsData.map((item) => item.parentClientDesc)),
        ).map((item) => ({ parentClientDesc: item }));

        uniqueParentClients.sort((a, b) => {
          if (!a.parentClientDesc) return 1;
          if (!b.parentClientDesc) return -1;
          return a.parentClientDesc.localeCompare(b.parentClientDesc);
        });

        setParentClientList(uniqueParentClients);

        setRowData([]);
        if (response.data?.obtManagementInfoResponses) {
          const validatedData = response.data.obtManagementInfoResponses.map(
            (item, index) => ({
              ...item,
              id: item.obtManagementInfoId,
              rowIndex: index + 1,
              lpPortfolioId:
                item.lpPortfolioId ||
                item.LP_PortfolioId ||
                item.lP_PortfolioId ||
                4,
            }),
          );

          setRowData(validatedData);
        }
      }
    } catch {
      setSnack({
        open: true,
        message: "Error occurred while fetching required data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleParentClientChange = async (event, value) => {
    try {
      setLoading(true);

      const parentClient = value || { parentClientDesc: "" };
      setSelectedParentClient(parentClient);
      setFilterProps(defaultFilterProps);
      await fetchData(parentClient);

      const updatePageSize = () => {
        setGridPageSize(calculatePageSize());
      };

      updatePageSize();

      window.addEventListener("resize", updatePageSize);
    } catch {
      setSnack({
        open: true,
        message: "Error occurred while fetching required data.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRecordByStatus = (event, value) => {
    try {
      setLoading(true);
      setShowRecordByStatus(value);
    } catch {
      setSnack({
        open: true,
        message: "Error occurred while changing record status.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const onRowSelectChange = (event) => {
    try {
      const newSelection = event.value || [];

      if (newSelection.length > MAX_BULK_EDIT_RECORDS) {
        setSnack({
          open: true,
          message: `You can only select up to ${MAX_BULK_EDIT_RECORDS} records for bulk edit.`,
          severity: "warning",
        });
        setSelectedRows(newSelection.slice(0, MAX_BULK_EDIT_RECORDS));
      } else {
        setSelectedRows(newSelection);
      }
    } catch {
      setSnack({
        open: true,
        message: "Error occurred while changing row selection.",
        severity: "error",
      });
    }
  };

  const handleBulkEdit = () => {
    try {
      setLoading(true);
      if (!editAccess) {
        setSnack({
          open: true,
          message: "No permissions to edit",
          severity: "warning",
        });
        return;
      }

      if (selectedRows.length === 0) {
        setSnack({
          open: true,
          message: "Please select at least one record",
          severity: "warning",
        });
        return;
      }

      if (selectedRows.length > MAX_BULK_EDIT_RECORDS) {
        setSnack({
          open: true,
          message: `You can only bulk edit up to ${MAX_BULK_EDIT_RECORDS} records at a time. Please narrow your selection.`,
          severity: "warning",
        });
        return;
      }

      const obtIds = selectedRows
        .map((item) => item.obtManagementInfoId)
        .join(",");
      navigate("/obtinfo/bulkEditAccountOwnerShip", {
        state: {
          OBTIds: [obtIds],
          parentClient: selectedParentClient,
        },
      });
    } catch {
      setSnack({
        open: true,
        message: "Error occured while changing record status.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditByRowNumbers = () => {
    setModalType("EditByRowIndex");
    setOpen(true);
  };

  const handleClose = async () => {
    setOpen(false);
  };

  const handleBulkEditRedirect = (selectedIds) => {
    try {
      if (!editAccess) {
        setSnack({
          open: true,
          message: "No permissions to edit",
          severity: "warning",
        });
        return;
      }

      if (selectedIds.length === 0) {
        setSnack({
          open: true,
          message: "Please select at least one record",
          severity: "warning",
        });
        return;
      }

      if (selectedIds.length > MAX_BULK_EDIT_RECORDS) {
        setSnack({
          open: true,
          message: `You can only bulk edit up to ${MAX_BULK_EDIT_RECORDS} records at a time. Please narrow your row range.`,
          severity: "warning",
        });
        return;
      }

      navigate("/obtinfo/bulkEditAccountOwnerShip", {
        state: {
          OBTIds: selectedIds,
          parentClient: selectedParentClient,
        },
      });
    } catch (error) {
      setSnack({
        open: true,
        message: "Error occurred while redirecting for bulk edit.",
        severity: "error",
      });
    }
  };

  const getOBTInfoId = (rowIndex) => {
    if (!rowData || rowData.length === 0) {
      setSnack({
        open: true,
        message: "Rows array is undefined or empty.",
        severity: "error",
      });
      return null;
    }

    const result = rowData.find((item) => item.rowIndex === rowIndex);
    return result ? result.obtManagementInfoId : null;
  };

  const handleEditByRowRedirect = (fromIndex, toIndex) => {
    try {
      const selectedIdsArray = Array.from(
        { length: toIndex - fromIndex + 1 },
        (_, index) => getOBTInfoId(fromIndex + index),
      );

      handleBulkEditRedirect(selectedIdsArray);
    } catch {
      setSnack({
        open: true,
        message: "Error occurred while redirecting for bulk edit.",
        severity: "error",
      });
    }
  };

  const openInPopup = (params, modalType) => {
    if (modalType === "buisnessservices") {
      setProjectFullCode(params.bsTask);
    }
    setOpen(true);
  };

  const handleSave = async (taData = null, ldData = null) => {
    setLoading(true);

    const {
      obtManagementInfoId,
      parentClientDesc,
      clientDesc,
      programDesc,
      projectDesc,
      projectCodeFull,
      bsTask,
      idmSupport,
      ogSupported,
      idmDeploymentStatus,
      rampDownDate,
      rosterLockBusDays,
      rosterLockFrom,
      ttecAccessSLABusDays,
      clientAccessSLABusDays,
      clientAccessRequestType,
      primaryIDMPOC,
      backupIDMPOC,
      clientDistro,
      clientOffboarding,
      idmTeamLead,
      idmTeamEmail,
      primaryOGs,
      backupOGs,
      idmOwner,
      idmEscalationPoint,
      switchIDPOC,
      ogOwner,
      ogEscalationPoint,
      ogTeamEmail,
      contactNumber,
      gender,
      nationalIdentifier,
      dateOfBirth,
      country,
      recruiter,
      token,
      confirmPreviousEmploymentWithClient,
      nickname,
      licenseNPN,
      productTrainingDate,
      address,
      city,
      state,
      postalCode,
      availability,
      inboundOutbound,
      language,
      integrationDate,
      email,
      nestingDate,
      productionDate,
      backgroundStatusDate,
      personalEmail,
      modifiedBy,
    } = details;

    const payload = {
      obtManagementInfoId,
      parentClientDesc,
      clientDesc,
      programDesc,
      projectDesc,
      projectCodeFull,
      bsTask,
      idmSupport,
      ogSupported,
      idmDeploymentStatus,
      rampDownDate,
      rosterLockBusDays,
      rosterLockFrom,
      ttecAccessSLABusDays,
      clientAccessSLABusDays,
      clientAccessRequestType,
      primaryIDMPOC,
      backupIDMPOC,
      clientDistro,
      clientOffboarding,
      idmTeamLead,
      idmTeamEmail,
      primaryOGs,
      backupOGs,
      idmOwner,
      idmEscalationPoint,
      switchIDPOC,
      ogOwner,
      ogEscalationPoint,
      ogTeamEmail,
      contactNumber,
      gender,
      nationalIdentifier,
      dateOfBirth,
      country,
      recruiter,
      token,
      confirmPreviousEmploymentWithClient,
      nickname,
      licenseNPN,
      productTrainingDate,
      address,
      city,
      state,
      postalCode,
      availability,
      inboundOutbound,
      language,
      integrationDate,
      email,
      nestingDate,
      productionDate,
      backgroundStatusDate,
      personalEmail,
      modifiedBy,

      primaryTAOracleId: taData?.primaryTAOracleId ?? details.primaryTAOracleId,
      backupTAOracleId: taData?.backupTAOracleId ?? details.backupTAOracleId,
      taTeamEmail: taData?.taTeamEmail ?? details.taTeamEmail,

      ldExecutiveOracleId:
        ldData?.ldExecutiveOracleId ??
        details.ldExecutiveOracleId,
      ldLevel1OracleId:
        ldData?.ldLevel1OracleId ?? details.ldLevel1OracleId,
      ldLevel2OracleId:
        ldData?.ldLevel2OracleId ?? details.ldLevel2OracleId,
      ldLevel3OracleId:
        ldData?.ldLevel3OracleId ?? details.ldLevel3OracleId,
      ldLevel4OracleId:
        ldData?.ldLevel4OracleId ?? details.ldLevel4OracleId,
      LP_PortfolioId: ldData?.lpPortfolioId ?? details.lpPortfolioId, // ← FIXED
    };

    try {
      const response = await saveOBTInfo([payload]);
      if (response.message === "Success") {
        handleClose();
        await fetchData(selectedParentClient);

        // Show appropriate success message based on modal type
        const successMessage =
          modalType === "tasupport"
            ? "TA Support details saved successfully."
            : modalType === "ldsupport"
              ? "L&D Support details saved successfully."
              : "Changes saved successfully.";

        setSnack({
          open: true,
          message: successMessage,
          severity: "success",
        });
      } else {
        setSnack({
          open: true,
          message: "Failed to Save",
          severity: "error",
        });
      }
    } catch {
      setSnack({
        open: true,
        message: "Something went wrong!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const ldInitialData = useMemo(
    () => ({
      ldExecutiveOracleId: details.ldExecutiveOracleId || null,
      ldExecutiveName: details.ldExecutiveName || null,
      ldLevel1OracleId: details.ldLevel1OracleId || null,
      ldLevel1Name: details.ldLevel1Name || null,
      ldLevel2OracleId: details.ldLevel2OracleId || null,
      ldLevel2Name: details.ldLevel2Name || null,
      ldLevel3OracleId: details.ldLevel3OracleId || null,
      ldLevel3Name: details.ldLevel3Name || null,
      ldLevel4OracleId: details.ldLevel4OracleId || null,
      ldLevel4Name: details.ldLevel4Name || null,
      lpPortfolioId: details.lpPortfolioId || 4,
    }),
    [
      details.ldExecutiveOracleId,
      details.ldExecutiveName,
      details.ldLevel1OracleId,
      details.ldLevel1Name,
      details.ldLevel2OracleId,
      details.ldLevel2Name,
      details.ldLevel3OracleId,
      details.ldLevel3Name,
      details.ldLevel4OracleId,
      details.ldLevel4Name,
      details.lpPortfolioId,
    ],
  );

  const taInitialData = useMemo(
    () => ({
      primaryTAOracleId: details.primaryTAOracleId || null,
      backupTAOracleId: details.backupTAOracleId || null,
      taTeamEmail: details.taTeamEmail || "",
    }),
    [details.primaryTAOracleId, details.backupTAOracleId, details.taTeamEmail],
  );

  return (
    <>
      <Paper
        sx={{
          flexDirection: "column",
          boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.35)",
          padding: 3,
          overflow: "auto",
          height: "calc(100vh - 80px)",
          width: "calc(100vw - 125px)",
        }}
      >
        {loading && <BackdropComponent />}
        <Grid container spacing={2} item xs={12}>
          <Grid item xs={12}>
            <Typography
              sx={{
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                fontSize: 18,
                fontWeight: "bold",
                color: "#3F5765",
                letterSpacing: "0.10em",
              }}
            >
              Account Ownership
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <Typography
              sx={{
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                fontWeight: "bold",
                color: "#3F5765",
                letterSpacing: "0.10em",
                fontSize: 12,
                marginTop: 0.75,
                marginBottom: 0.75,
              }}
            >
              Parent Client
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControl fullWidth>
                <Autocomplete
                  disablePortal
                  options={parentClientList}
                  value={selectedParentClient || null}
                  getOptionLabel={(option) => option.parentClientDesc || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.parentClientDesc === value.parentClientDesc
                  }
                  onChange={handleParentClientChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      name="parentClient"
                      size="small"
                      variant="outlined"
                      placeholder="Parent Client"
                    />
                  )}
                />
              </FormControl>
              <CustomButton
                variant="contained"
                onClick={handleBulkEdit}
                disabled={rowData.length === 0 || !editAccess}
              >
                Edit
              </CustomButton>
              <CustomOutlinedButton
                variant="outlined"
                onClick={handleEditByRowNumbers}
                sx={{ minWidth: 125 }}
                disabled={rowData.length === 0 || !editAccess}
              >
                Edit by Row #
              </CustomOutlinedButton>
            </Box>
          </Grid>
          <Grid item xs={6}>
            &nbsp;
          </Grid>
          <Grid item xs={1}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  fontWeight: "bold",
                  color: "#3F5765",
                  letterSpacing: "0.10em",
                  fontSize: 12,
                  marginTop: 0.75,
                  marginBottom: 0.75,
                }}
              >
                {showRecordByStatus ? "Show Active" : "Show Inactive"}
              </Typography>
              <AntSwitch
                checked={showRecordByStatus}
                disabled={true}
                onChange={handleRecordByStatus}
                inputProps={{ "aria-label": "ant design" }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} className={classes.primeReactGridContainer}>
            <PrimeReactDataGrid
              enableScrollable={true}
              enableStickyHeader={true}
              scrollHeight="calc(100vh - 370px)"
              dataKey="id"
              customtitle={selectedParentClient.parentClientDesc}
              size="normal"
              removableSort
              resizableColumns
              showGridlines
              paginator
              rows={gridPageSize}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} record(s)"
              columnResizeMode="expand"
              value={rowData}
              columns={columns}
              filterprops={filterProps}
              onFilter={(e) => setFilterProps(e.filters)}
              selectionMode="multiple"
              dragSelection
              selection={selectedRows}
              metaKeySelection={false}
              onSelectionChange={onRowSelectChange}
              globalFilterFields={[
                "clientDesc",
                "programDesc",
                "projectDesc",
                "projectCodeFull",
                "idmDeploymentStatus",
                "rampDownDate",
                "rosterLockBusDays",
                "rosterLockFrom",
              ]}
              sortMode="multiple"
              emptyMessage="No records found."
            ></PrimeReactDataGrid>
          </Grid>
        </Grid>
        <SnackBar snack={snack} snackReset={snackReset} />
      </Paper>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: "80%",
            maxWidth: "600px",
          }}
        >
          {modalType === "buisnessservices" && (
            <SaveBSTaskDetails
              onClose={handleClose}
              projectFullCode={projectFullCode}
              isEdit={editAccess}
            />
          )}
          {modalType === "idmSupport" && (
            <IDMSupportModalContent
              onClose={handleClose}
              handleSave={handleSave}
              loading={loading}
              isEdit={editAccess}
            />
          )}
          {modalType === "ogguidesupport" && (
            <OnboardingModalContent
              onClose={handleClose}
              handleSave={handleSave}
              loading={loading}
              isEdit={editAccess}
            />
          )}
          {modalType === "offboardingnotification" && (
            <OffboardingModalContent
              onClose={handleClose}
              handleSave={handleSave}
              loading={loading}
              isEdit={editAccess}
            />
          )}
          {modalType === "access" && (
            <AccessModalContent
              onClose={handleClose}
              handleSave={handleSave}
              loading={loading}
              isEdit={editAccess}
            />
          )}
          {modalType === "obtrequirements" && (
            <OBTRequirementsModalContent
              onClose={handleClose}
              handleSave={handleSave}
              handleEdit={handleEditByRowRedirect}
              loading={loading}
              isEdit={editAccess}
            />
          )}
          {modalType === "tasupport" && (
            <TASupportModalContent
              onClose={handleClose}
              handleSave={handleSave}
              loading={loading}
              isEdit={editAccess}
              initialData={taInitialData}
            />
          )}
          {modalType === "ldsupport" && (
            <LDSupportModalContent
              onClose={handleClose}
              handleSave={(ldData) => handleSave(null, ldData)}
              loading={loading}
              isEdit={editAccess}
              initialData={ldInitialData}
            />
          )}
          {modalType === "EditByRowIndex" && (
            <EditByRowIndex
              onClose={handleClose}
              handleEdit={handleEditByRowRedirect}
              maxRowIndex={rowData.length}
            />
          )}
        </Box>
      </Modal>
    </>
  );
}

export default IDMOGOwnerShip;
